import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import portalT from '../translations/portal';
import axios from 'axios';
import { ArrowLeft, Bot, Loader } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import ExportPDFButton from '../components/ExportPDFButton';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const AgentBuilder = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const { lang } = useLanguage();
  const t = portalT[lang];
  const tp = t.toolPages.agent;

  const [formData, setFormData] = useState({
    agent_name: '', description: '', model_provider: 'gemini',
    model_name: 'gemini-2.0-flash', system_prompt: '', use_case: '',
  });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const modelOptions = {
    openai: ['gpt-5.2', 'gpt-5.1', 'gpt-4.1', 'o3'],
    anthropic: ['claude-sonnet-4-6', 'claude-opus-4-6', 'claude-4-sonnet-20250514'],
    gemini: ['gemini-2.0-flash', 'gemini-2.5-pro', 'gemini-2.5-flash'],
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await axios.post(`${API}/tools/agent-builder`, formData, { headers: { Authorization: `Bearer ${token}` } });
      setResult(res.data);
    } catch (error) {
      alert('Error: ' + (error.response?.data?.detail || error.message));
    } finally { setLoading(false); }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'model_provider') {
      setFormData({ ...formData, [name]: value, model_name: modelOptions[value][0] });
    } else {
      setFormData({ ...formData, [name]: value });
    }
  };

  return (
    <div className="tool-page">
      <div className="tool-header">
        <button onClick={() => navigate('/portal/ai-tools')} className="btn-secondary" data-testid="back-btn"><ArrowLeft size={16} /> {t.common.aiTools}</button>
        <h1 className="heading-1" data-testid="agent-title">{tp.title}</h1>
      </div>
      <div className="tool-content">
        {!result ? (
          <div className="tool-form-container">
            <div className="tool-intro"><Bot size={32} /><h2 className="heading-2">{tp.intro}</h2><p className="body-medium text-secondary">{tp.introDesc}</p></div>
            <form onSubmit={handleSubmit} className="tool-form">
              <div className="form-group"><label className="body-small">{tp.agentName}</label><input type="text" name="agent_name" value={formData.agent_name} onChange={handleChange} required className="form-input" placeholder={tp.agentPlaceholder} data-testid="input-agent-name" /></div>
              <div className="form-group"><label className="body-small">{tp.description}</label><textarea name="description" value={formData.description} onChange={handleChange} required rows="3" className="form-input" placeholder={tp.descPlaceholder} data-testid="input-desc" /></div>
              <div className="form-row">
                <div className="form-group"><label className="body-small">{tp.provider}</label><select name="model_provider" value={formData.model_provider} onChange={handleChange} className="form-input" data-testid="select-provider"><option value="gemini">Google Gemini</option><option value="openai">OpenAI GPT</option><option value="anthropic">Anthropic Claude</option></select></div>
                <div className="form-group"><label className="body-small">{tp.model}</label><select name="model_name" value={formData.model_name} onChange={handleChange} className="form-input" data-testid="select-model">{modelOptions[formData.model_provider].map(m => <option key={m} value={m}>{m}</option>)}</select></div>
              </div>
              <div className="form-group"><label className="body-small">{tp.systemPrompt}</label><textarea name="system_prompt" value={formData.system_prompt} onChange={handleChange} required rows="5" className="form-input" placeholder={tp.systemPlaceholder} data-testid="input-prompt" /></div>
              <div className="form-group"><label className="body-small">{tp.useCase}</label><textarea name="use_case" value={formData.use_case} onChange={handleChange} required rows="4" className="form-input" placeholder={tp.useCasePlaceholder} data-testid="input-usecase" /></div>
              <button type="submit" disabled={loading} className="btn-primary submit-btn" data-testid="submit-btn">{loading ? <><Loader className="spin" size={16} /> {tp.building}</> : <><Bot size={16} /> {tp.build}</>}</button>
            </form>
          </div>
        ) : (
          <div className="tool-result">
            <div className="result-header"><h2 className="heading-2">{tp.resultTitle}</h2><div style={{display:'flex',gap:'0.5rem'}}><ExportPDFButton title={tp.resultTitle} content={result.agent_design} toolName={tp.title} /><button onClick={() => setResult(null)} className="btn-secondary" data-testid="build-another-btn">{tp.buildAnother}</button></div></div>
            <div className="result-content markdown-content"><ReactMarkdown>{result.agent_design}</ReactMarkdown></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentBuilder;
